import { Aurelia, autoinject, computedFrom } from 'aurelia-framework';
import { ApiService } from '../../../utils/api-service';
import { json } from 'aurelia-fetch-client';
import { ResponseHandler } from '../../../utils/response-handler';
import { reject, filter } from 'lodash';

export interface IRequiredDimensionValueAM {
	dimensionId: string;
	dimensionValueId: string;
	dimensionName: string;
	valueIdentifier: string;
	valueName: string;
}

export interface ICostCenterConditionTargetingModel {
	id: string;
	costCenterId: string;
	fiscalYearId: string;
	requiredDimensionValues: RequiredDimensionValue[];
	isCollapsed: boolean;
}

export interface ICostCenterServiceLinkAM {
	id: string;
	costCenterId: string;
	identifier: string;
	title: string;
	isCostCenterIgnored: boolean;
	collectMeasures: boolean;
	serviceId: string;
	serviceName: string;
	fiscalYearId: string;
	accountingDimensionSuffix: string;
	dimensionValueIds: string[];
}

export interface IAutomaticTargetingBypassAM {
	isBypassed: boolean;
	canEdit: boolean;
}

export interface ITargetToServiceAM {
	costCenterId: string;
	serviceId: string;
	serviceTargetingId: string;
}

export class CostCenterServiceLink implements ICostCenterServiceLinkAM {
	id: string;
	costCenterId: string;
	identifier: string;
	title: string;
	isCostCenterIgnored: boolean;
	collectMeasures: boolean;
	serviceId: string;
	serviceName: string;
	fiscalYearId: string;
	accountingDimensionSuffix: string;
	dimensionValueIds: string[];

	constructor(init: Partial<CostCenterServiceLink>) {
		Object.assign(this, init);
	}

	toString(): string { // todo: nimeksi esim. FormatCostCenterAndDimensions
		let value = `${this.identifier} ${this.title}`;
		if (this.accountingDimensionSuffix) {
			value += ` (${this.accountingDimensionSuffix})`;
		}
		return value;
	}

	formatServiceNameWithDimensionSuffix(): string {
		let value = this.serviceName;
		if (this.accountingDimensionSuffix) {
			value += ` (${this.accountingDimensionSuffix})`
		}
		return value;
	}

	static createNew(data: ICostCenterServiceLinkAM): CostCenterServiceLink {
		return new CostCenterServiceLink(data);
	}
}

export class RequiredDimensionValue implements IRequiredDimensionValueAM {
	dimensionId: string;
	dimensionValueId: string;
	dimensionName: string;
	valueIdentifier: string;
	valueName: string;

	get displayText(): string {
		return `${this.dimensionName} = ${this.valueIdentifier} ${this.valueName}`;
	}

	static createNew(data: IRequiredDimensionValueAM): RequiredDimensionValue {
		const model = new RequiredDimensionValue();
		model.dimensionId = data.dimensionId;
		model.dimensionValueId = data.dimensionValueId;
		model.dimensionName = data.dimensionName;
		model.valueName = data.valueName;
		model.valueIdentifier = data.valueIdentifier;
		return model;
	}

	get validateForInsert(): boolean {
		const dimensionValueSet = !!this.dimensionValueId;
		const dimensionIdSet = !!this.dimensionId;
		return dimensionIdSet && dimensionValueSet;
	}
}

export class CostCenterConditionTargetingModel {
	id: string;
	costCenterId: string;
	fiscalYearId: string;
	requiredDimensionValues: RequiredDimensionValue[];
	isCollapsed: boolean;
	isIgnored: boolean;

	constructor() {
		this.requiredDimensionValues = [];
	}

	@computedFrom('requiredDimensionValues')
	get dimensionValueIdsCommaSeparated(): string {
		return this.requiredDimensionValues.map(c => c.dimensionValueId).join(",");
	}

	@computedFrom('requiredDimensionValues')
	get formattedName(): string {
		return this.requiredDimensionValues.map(v => v.displayText).sort().join(" & ");
	}

	static createNew(data: ICostCenterConditionTargetingModel): CostCenterConditionTargetingModel {
		const model = new CostCenterConditionTargetingModel();
		model.id = data.id;
		model.fiscalYearId = data.fiscalYearId;
		model.costCenterId = data.costCenterId;
		model.isCollapsed = true;
		data.requiredDimensionValues.map(item => model.requiredDimensionValues.push(RequiredDimensionValue.createNew(item)));
		return model;
	}
}

@autoinject
export class LinkedMappingsService {
	costCenterServiceLinksCache: CostCenterServiceLink[];
	costCenterConditionsCache: CostCenterConditionTargetingModel[];

	constructor(private http: ApiService, private responseHandler: ResponseHandler) {
		this.costCenterServiceLinksCache = [];
		this.costCenterConditionsCache = [];
	}

	async fetchLinksForFiscalYear(fiscalYearId: string, serviceStructureId: string, includeIgnoredCostCenters: boolean = false, parentServiceId: string = null): Promise<CostCenterServiceLink[]> {

		let url = `service-targetings/${fiscalYearId}/for-service-structure/${serviceStructureId}/cost-centers/linked?includeIgnoredCostCenters=${includeIgnoredCostCenters}`;
		if (parentServiceId) {
			url += `&parentServiceId=${parentServiceId}`;
		}
		return this.http.fetchJson(url)
			.then((data) => data.map(CostCenterServiceLink.createNew));
	}

	async refreshLinksCache(fiscalYearId: string, serviceStructureId: string, includeIgnoredCostCenters: boolean = false, parentServiceId: string = null): Promise<any> {
		this.costCenterServiceLinksCache = await this.fetchLinksForFiscalYear(fiscalYearId, serviceStructureId, includeIgnoredCostCenters, parentServiceId);
	}

	refreshCostCenterConditionsCache(fiscalYearId: string, serviceStructureId: string): Promise<any> {
		return this.http.fetchJson(`service-targetings/${fiscalYearId}/for-service-structure/${serviceStructureId}/targeting-conditions`)
			.then((data) => this.costCenterConditionsCache = data.map(CostCenterConditionTargetingModel.createNew));
	}

	getLinksOfTargeting(costCenterId: string, dimensionValueIds: string[]): CostCenterServiceLink[] {

		if (!this.costCenterServiceLinksCache) {
			return [];
		}

		return filter(this.costCenterServiceLinksCache,
			(link: CostCenterServiceLink) => (link.costCenterId === costCenterId) && this.hasCorrectDimensionValues(link, dimensionValueIds)
		);
	}

	private hasCorrectDimensionValues(link: CostCenterServiceLink, dimensionValueIdsArray: string[]): boolean {

		if (!dimensionValueIdsArray || dimensionValueIdsArray.length == 0) {
			return !link.dimensionValueIds || link.dimensionValueIds.length == 0;
		}

		if (dimensionValueIdsArray.length != link.dimensionValueIds.length) {
			return false;
		}

		return dimensionValueIdsArray.every(id => link.dimensionValueIds.indexOf(id) >= 0);
	}

	async removeLinkedCostCenter(costCenterServiceLink: CostCenterServiceLink, serviceStructureId: string) {
		const response = await this.http.fetch(`service-targetings/${costCenterServiceLink.fiscalYearId}/for-service-structure/${serviceStructureId}/cost-centers/linked/${costCenterServiceLink.id}`, {
			method: 'delete'
		});

		if (this.responseHandler.isSuccess(response.status)) {
			this.costCenterServiceLinksCache = this.costCenterServiceLinksCache.filter((item) => item.id !== costCenterServiceLink.id);
		}
	}

	insertCostCenterCondition(
		fiscalYearId: string,
		serviceStructureId: string, 
		costCenterId: string,
		requiredDimensionValues: RequiredDimensionValue[]): Promise<any> {

		return this.http.fetch(`service-targetings/${fiscalYearId}/for-service-structure/${serviceStructureId}/targeting-conditions/${costCenterId}`, {
			method: 'post',
			body: json(requiredDimensionValues)
		});
	}

	async removeCostCenterCondition(condition: CostCenterConditionTargetingModel, servicestructureid: string) {
		const response = await this.http.fetch(`service-targetings/${condition.fiscalYearId}/for-service-structure/${servicestructureid}/targeting-conditions`, {
			method: 'delete',
			body: json(condition)
		});

		if (this.responseHandler.isSuccess(response.status)) {
			this.costCenterConditionsCache = this.costCenterConditionsCache.filter((item) => item.id !== condition.id);
		}
	}

	toggleCollectMeasuresForCostCenter(link: CostCenterServiceLink, collectMeasures: boolean, serviceStructureId: string): Promise<any> {
		return this.http.fetch(`service-targetings/${link.fiscalYearId}/for-service-structure/${serviceStructureId}/cost-centers/linked/${link.id}/collect-measures/${collectMeasures}`, {
			method: 'put'
		});
	}

	toggleCollectMeasuresForCondition(condition: CostCenterConditionTargetingModel, serviceId: string, collectMeasures: boolean, serviceStructureId: string): Promise<any> {
		return this.http.fetch(`service-targetings/${condition.fiscalYearId}/for-service-structure/${serviceStructureId}/targeting-conditions/${condition.id}/collect-measures-for-service/${serviceId}/${collectMeasures}`, {
			method: 'put'
		});
	}

	/** Resolves to null when the request fails. */
	getAutomaticTargetingBypass(fiscalYearId: string, serviceStructureId: string): Promise<IAutomaticTargetingBypassAM | null> {
		return this.http.fetchJson(`service-targetings/${fiscalYearId}/for-service-structure/${serviceStructureId}/automatic-targeting-bypass`, undefined, () => { });
	}

	/** Resolves to null when the request fails. */
	setAutomaticTargetingBypass(fiscalYearId: string, serviceStructureId: string, isBypassed: boolean): Promise<IAutomaticTargetingBypassAM | null> {
		return this.http.fetchJson(`service-targetings/${fiscalYearId}/for-service-structure/${serviceStructureId}/automatic-targeting-bypass/${isBypassed}`, {
			method: 'post'
		}, () => { });
	}

	async setCostCenterIsIgnoredStatus(costCenterId: string, isIgnored: boolean) {
		return await this.http.fetch(`cost-centers/${costCenterId}/is-ignored/${isIgnored}`, {
			method: 'post'
		}).then(() => {
			this.costCenterServiceLinksCache
				.filter((link) => link.costCenterId === costCenterId)
				.forEach((link) => link.isCostCenterIgnored = isIgnored);
			this.costCenterServiceLinksCache = this.costCenterServiceLinksCache.slice(0); // to trigger computedFrom recalculation
		});
	}

	async updateMappings(fiscalYearId: string, serviceStructureId: string, targetToService: ITargetToServiceAM[]): Promise<any> {
		return await this.http.fetch(`service-targetings/${fiscalYearId}/for-service-structure/${serviceStructureId}`, {
			method: 'post',
			body: json(targetToService)
		});
	}
}
